import { Link } from "react-router-dom";
import { Heart, Users, Sparkles } from "lucide-react";

const FOUNDER = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/db9df231-7ec5-4a7f-90ec-ee5b58d79a2a/0M3A4752.jpg";
const TABLE_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/e10d0153-5fbb-484e-b9da-2908a1ba4b5c/wmwe+pic+me+and+jania.jpg";

export default function WMWEAbout() {
    return (
        <main data-testid="wmwe-about" className="bg-sanct-sand text-sanct-charcoal">
            {/* SECTION 1 — Origin Story (two-column) */}
            <section className="py-28 md:py-36 bg-sanct-cream">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <img src={TABLE_IMG} alt="WMWE table" className="w-full aspect-[4/5] object-cover rounded-sm" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Origin Story</p>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                            It began
                            <span className="block italic text-sanct-rose mt-2">with eight women.</span>
                        </h1>
                        <p className="font-serif text-xl md:text-2xl text-sanct-cocoa mt-10 leading-[1.5]">
                            On <span className="italic">November 14, 2019</span>, Shana gathered eight women around a table — not for networking, not for business, but for connection.
                        </p>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/80 mt-6 leading-[1.8]">
                            She wanted to create an atmosphere where connection was easy, shared experiences were welcome, vulnerability was safe, and community was natural.
                        </p>
                        <p className="font-sans text-base text-sanct-charcoal/75 mt-5 leading-[1.8]">
                            And it worked. Women connected deeply. Stories were shared. Hearts opened. She kept hosting these gatherings until the world shut down — and then WMWE went virtual.
                        </p>
                        <p className="font-serif italic text-2xl text-sanct-rose mt-8 leading-tight">
                            What began as a lunch became a movement.
                        </p>
                    </div>
                </div>
            </section>

            {/* IMPACT — served 200+ women */}
            <section className="py-24 md:py-32 bg-sanct-cocoa text-sanct-cream">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Impact</p>
                    <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
                        More than
                        <span className="italic text-sanct-rose"> 200 women</span> gathered.
                    </h2>
                    <p className="font-serif italic text-xl md:text-2xl text-sanct-cream/85 mt-10 leading-[1.6] max-w-[820px] mx-auto">
                        Educators. Corporate leaders. Realtors. Entrepreneurs building 5&#8209; and 6&#8209;figure businesses. Women of purpose from Charlotte, NC and the surrounding region &mdash; all pulled up a chair, and all left changed.
                    </p>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-[900px] mx-auto">
                        <ImpactStat number="200+" label="Women gathered" />
                        <ImpactStat number="5+" label="Years of fellowship" />
                        <ImpactStat number="1" label="City that grew us" sub="Charlotte, NC" />
                        <ImpactStat number="∞" label="Prayers answered" />
                    </div>
                </div>
            </section>

            {/* SECTION 2 — The Calling (full-width, centered, soft bg) */}
            <section className="py-28 md:py-36 bg-sanct-clay/30 text-center">
                <div className="max-w-[960px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Calling</p>
                    <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                        Today, WMWE is becoming an
                        <span className="block italic text-sanct-rose mt-2">ecosystem of women</span>
                    </h2>
                    <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa/85 mt-10 leading-[1.6]">
                        who learn together, grow together, worship together, and commune together.
                    </p>
                    <div className="mt-14 flex flex-wrap gap-x-10 gap-y-3 justify-center font-serif italic text-2xl md:text-3xl text-sanct-rose">
                        <span>A fellowship.</span>
                        <span>A sanctuary.</span>
                        <span>A sisterhood.</span>
                    </div>
                </div>
            </section>

            {/* SECTION 3 — The Vision (3 pillars, rounded cards) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <div className="text-center max-w-[800px] mx-auto mb-14">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">The Vision</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight">
                            A global fellowship of women who walk in
                            <span className="italic text-sanct-rose"> purpose, connection, and spiritual alignment.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { icon: Heart, title: "Purpose", body: "Live anchored in what God is calling you to — with clarity and conviction." },
                            { icon: Users, title: "Connection", body: "Build life-giving relationships that reflect your values and your faith." },
                            { icon: Sparkles, title: "Spiritual Alignment", body: "Move through life with your heart, spirit, and calling in agreement." },
                        ].map((p) => {
                            const Icon = p.icon;
                            return (
                                <div key={p.title} className="bg-sanct-cream rounded-[24px] p-10 border border-sanct-clay/40 text-center">
                                    <div className="w-14 h-14 rounded-full bg-sanct-rose/20 flex items-center justify-center mx-auto mb-5">
                                        <Icon size={22} className="text-sanct-rose" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-serif text-2xl text-sanct-cocoa mb-3">{p.title}</h3>
                                    <p className="font-sans text-sm text-sanct-charcoal/75 leading-[1.7]">{p.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — Meet Shana */}
            <section className="py-24 md:py-32 bg-sanct-clay/30">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <img src={FOUNDER} alt="Shana Price" className="w-full aspect-[4/5] object-cover rounded-sm" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Meet Shana</p>
                        <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Founder. Steward. <span className="italic text-sanct-rose">Connector.</span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/85 mt-8 leading-[1.8]">
                            A woman called to gather women and create spaces where God can move.
                        </p>
                        <p className="font-serif italic text-xl text-sanct-cocoa/80 mt-6 leading-[1.5]">
                            &ldquo;God is a relationship; we are relationship people.&rdquo;
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 5 — CTA */}
            <section className="py-28 md:py-36 bg-sanct-cream text-center">
                <h2 className="font-serif italic text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight px-6">
                    Come be part of the <span className="text-sanct-rose">fellowship.</span>
                </h2>
                <Link
                    to="/wmwe/fellowship#waitlist"
                    data-testid="wmwe-about-join-btn"
                    className="inline-flex items-center mt-12 px-9 py-5 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors duration-300"
                >
                    Join the Fellowship
                </Link>
            </section>
        </main>
    );
}

function ImpactStat({ number, label, sub }) {
    return (
        <div data-testid={`impact-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="font-serif text-5xl md:text-6xl text-sanct-rose leading-none">{number}</p>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cream/80 mt-3">{label}</p>
            {sub && <p className="font-sans text-xs text-sanct-cream/60 mt-1">{sub}</p>}
        </div>
    );
}
